import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import ReactFlow, { 
  MiniMap, 
  Controls, 
  Background,
  useNodesState,
  useEdgesState,
  MarkerType,
  Handle,
  Position
} from 'reactflow';
import 'reactflow/dist/style.css';
import './OrgChart.css';
import { Button } from '../components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { ArrowLeft, ChevronDown, ChevronUp, Building2, Monitor } from 'lucide-react';
import { toast } from 'sonner';
import { usePermissions } from '../hooks/usePermissions';
import { useAuth } from '../contexts/AuthContext';
import AppLayout from '../components/AppLayout';
import useDeviceType from '../hooks/useDeviceType';
import { API_URL } from '../apiBase';

/** Pas de la grille visuelle React Flow (`<Background gap={…} />`) — coins des cartes alignés dessus. */
const ORG_CHART_GRID_SIZE = 20;
const ORG_CHART_SNAP_GRID = [ORG_CHART_GRID_SIZE, ORG_CHART_GRID_SIZE];

const snapPositionToGrid = (position) => {
  if (!position || typeof position.x !== 'number' || typeof position.y !== 'number') {
    return position;
  }
  return {
    x: Math.round(position.x / ORG_CHART_GRID_SIZE) * ORG_CHART_GRID_SIZE,
    y: Math.round(position.y / ORG_CHART_GRID_SIZE) * ORG_CHART_GRID_SIZE,
  };
};

const getPhotoUrl = (photo_url) => {
  if (!photo_url) return null;
  // Si c'est du base64, retourner tel quel
  if (photo_url.startsWith('data:')) return photo_url;
  // Si c'est une URL externe, retourner tel quel
  if (photo_url.startsWith('http')) return photo_url;
  // Sinon, ajouter l'URL du backend
  return `${API_URL}${photo_url}`;
};

const CustomNode = ({ data }) => {
  const navigate = useNavigate();
  
  const getInitials = (nom, prenom) => {
    const n = nom?.charAt(0) || '';
    const p = prenom?.charAt(0) || '';
    return `${n}${p}`.toUpperCase();
  };

  const handleCardClick = () => {
    navigate(`/user/${data.id}`);
  };

  const handleToggleClick = (e) => {
    e.stopPropagation();
    if (data.onToggleExpand) {
      data.onToggleExpand(data.id);
    }
  };

  const isCurrentUser = data.isCurrentUser;

  return (
    <div 
      onClick={handleCardClick}
      className={`bg-card rounded-md p-4 cursor-pointer transition-all relative ${
        isCurrentUser 
          ? 'border-4 border-primary shadow-lg shadow-primary/50' 
          : 'border-2 border-border hover:border-primary'
      }`}
      style={{ width: '250px' }}
    >
      <Handle type="target" position={Position.Top} />
      {isCurrentUser && (
        <div className="absolute -top-2 -right-2 bg-primary text-primary-foreground px-2 py-0.5 rounded-full text-xs font-semibold shadow-md z-10">
          Vous
        </div>
      )}
      <div className="flex items-center gap-3">
        <Avatar className="h-10 w-10">
          <AvatarImage src={getPhotoUrl(data.photo_url)} />
          <AvatarFallback>{getInitials(data.nom, data.prenom)}</AvatarFallback>
        </Avatar>
        <div className="flex-1 min-w-0">
          <p className="font-semibold truncate text-sm">
            {data.prenom} {data.nom}
          </p>
          {data.poste && (
            <p className="text-xs text-muted-foreground truncate">{data.poste}</p>
          )}
        </div>
        {data.hasChildren && (
          <button
            onClick={handleToggleClick}
            className="ml-auto p-1 rounded hover:bg-primary/10 transition-colors"
            title={data.isExpanded ? "Réduire" : "Développer"}
          >
            {data.isExpanded ? (
              <ChevronUp className="h-4 w-4 text-primary" />
            ) : (
              <ChevronDown className="h-4 w-4 text-primary" />
            )}
          </button>
        )}
      </div>
      {data.service && (
        <div className="mt-2 text-xs text-muted-foreground truncate">
          {data.service}
        </div>
      )}
      {data.societe && (
        <div className="mt-1">
          <span className="inline-block px-2 py-0.5 rounded bg-primary/10 text-primary text-xs">
            {data.societe}
          </span>
        </div>
      )}
      <Handle type="source" position={Position.Bottom} />
    </div>
  );
};

const nodeTypes = {
  custom: CustomNode
};

const OrgChartPage = () => {
  const navigate = useNavigate();
  const { isMobile, isDesktop } = useDeviceType();
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasCustomLayout, setHasCustomLayout] = useState(false);
  const [expandedNodes, setExpandedNodes] = useState(new Set());
  const [orgData, setOrgData] = useState([]);
  const [hierarchyMap, setHierarchyMap] = useState({});
  const [selectedCompanies, setSelectedCompanies] = useState(new Set());
  const [currentUserId, setCurrentUserId] = useState(null);
  const allNodePositionsRef = useRef({}); // Store ALL positions (visible or not) - using ref to avoid re-renders

  // Get permissions and user
  const { canModifyOrgchartLayout } = usePermissions();
  const { user } = useAuth();

  // Rediriger les utilisateurs mobiles vers le dashboard
  useEffect(() => {
    if (isMobile) {
      toast.error("L'organigramme n'est pas disponible sur mobile", {
        description: "Veuillez utiliser un ordinateur pour accéder à cette fonctionnalité."
      });
      navigate('/dashboard', { replace: true });
    }
  }, [isMobile, navigate]);

  // Si mobile, afficher un message de chargement pendant la redirection
  if (isMobile) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-full">
          <div className="text-center p-8 max-w-md">
            <div className="w-16 h-16 bg-orange-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Monitor className="h-8 w-8 text-orange-400" />
            </div>
            <h2 className="text-xl font-semibold text-foreground mb-2">
              Fonctionnalité Desktop uniquement
            </h2>
            <p className="text-muted-foreground text-sm">
              L'organigramme n'est pas disponible sur les appareils mobiles. 
              Veuillez utiliser un ordinateur (Windows, MacOS ou Linux) pour accéder à cette fonctionnalité.
            </p>
          </div>
        </div>
      </AppLayout>
    );
  }
  
  // Extract unique companies from hierarchy
  const availableCompanies = useMemo(() => {
    const companies = new Set();
    Object.values(hierarchyMap).forEach(nodeInfo => {
      if (nodeInfo.societe) {
        companies.add(nodeInfo.societe);
      }
    });
    return Array.from(companies).sort();
  }, [hierarchyMap]);

  useEffect(() => {
    fetchOrgChart();
  }, []);

  const buildHierarchyMap = (orgData) => {
    const map = {};
    
    const traverse = (node, parentId = null, level = 0) => {
      map[node.id] = {
        ...node,
        parentId,
        level,
        hasChildren: node.children && node.children.length > 0,
        childrenIds: node.children ? node.children.map(c => c.id) : []
      };
      
      if (node.children) {
        node.children.forEach(child => traverse(child, node.id, level + 1));
      }
    };
    
    orgData.forEach(root => traverse(root, null, 0));
    return map;
  };

  const getVisibleNodesAndEdges = useCallback((hierarchyMap, expandedNodes, storedPositions = {}, companyFilter = new Set(), currentUserId = null) => {
    const visibleNodeIds = new Set();
    const newNodes = [];
    const edges = [];
    const horizontalSpacing = 360; // multiple de ORG_CHART_GRID_SIZE (20)
    const verticalSpacing = 200;
    
    // Helper: check if a node matches the company filter
    const matchesCompanyFilter = (nodeId) => {
      if (companyFilter.size === 0) return false; // No company selected = show nothing
      const nodeInfo = hierarchyMap[nodeId];
      return nodeInfo.societe && companyFilter.has(nodeInfo.societe);
    };
    
    // Helper: check if any descendant matches the filter
    const hasMatchingDescendant = (nodeId, visited = new Set()) => {
      if (visited.has(nodeId)) return false;
      visited.add(nodeId);
      
      if (matchesCompanyFilter(nodeId)) return true;
      
      const nodeInfo = hierarchyMap[nodeId];
      if (nodeInfo.childrenIds && nodeInfo.childrenIds.length > 0) {
        return nodeInfo.childrenIds.some(childId => hasMatchingDescendant(childId, visited));
      }
      return false;
    };

    // Find all visible nodes based on expansion state AND company filter
    const addVisibleNodes = (nodeId) => {
      if (visibleNodeIds.has(nodeId)) return;
      
      // Include node if:
      // 1. It matches the company filter, OR
      // 2. It has descendants that match the filter (to maintain hierarchy)
      if (!matchesCompanyFilter(nodeId) && !hasMatchingDescendant(nodeId)) {
        return;
      }
      
      visibleNodeIds.add(nodeId);
      const nodeInfo = hierarchyMap[nodeId];
      
      // If this node is expanded, add its direct children (but only if they match filter or have matching descendants)
      if (expandedNodes.has(nodeId) && nodeInfo.hasChildren) {
        nodeInfo.childrenIds.forEach(childId => {
          // Only add children that pass the filter check
          if (matchesCompanyFilter(childId) || hasMatchingDescendant(childId)) {
            visibleNodeIds.add(childId);
          }
        });
      }
    };

    // Start with root nodes (level 0)
    Object.values(hierarchyMap).forEach(nodeInfo => {
      if (nodeInfo.level === 0) {
        addVisibleNodes(nodeInfo.id);
      }
    });

    // Now recursively add children of expanded nodes
    const processed = new Set();
    const processExpanded = () => {
      let added = false;
      visibleNodeIds.forEach(nodeId => {
        if (processed.has(nodeId)) return;
        processed.add(nodeId);
        
        const nodeInfo = hierarchyMap[nodeId];
        if (expandedNodes.has(nodeId) && nodeInfo.hasChildren) {
          nodeInfo.childrenIds.forEach(childId => {
            // Only add children that pass the filter check
            if (!visibleNodeIds.has(childId) && (matchesCompanyFilter(childId) || hasMatchingDescendant(childId))) {
              visibleNodeIds.add(childId);
              added = true;
            }
          });
        }
      });
      if (added) processExpanded();
    };
    processExpanded();

    // Build nodes - preserve existing positions, calculate only for new nodes
    const positionedNodes = {};
    const levelGroups = {};
    
    // Group visible nodes by level (for new nodes that need positions)
    visibleNodeIds.forEach(nodeId => {
      const nodeInfo = hierarchyMap[nodeId];
      if (!levelGroups[nodeInfo.level]) {
        levelGroups[nodeInfo.level] = [];
      }
      levelGroups[nodeInfo.level].push(nodeInfo);
    });

    // Create nodes, preserving stored positions but avoiding exact overlaps
    // (ex. utilisateur réactivé qui partage encore les coordonnées d'un collègue)
    const occupiedKeys = new Set();
    const positionKey = (pos) => `${pos.x},${pos.y}`;
    const claimFreePosition = (desired) => {
      let pos = snapPositionToGrid(desired);
      let guard = 0;
      while (occupiedKeys.has(positionKey(pos)) && guard < 200) {
        pos = snapPositionToGrid({
          x: pos.x + horizontalSpacing,
          y: pos.y,
        });
        guard += 1;
      }
      occupiedKeys.add(positionKey(pos));
      return pos;
    };

    visibleNodeIds.forEach(nodeId => {
      const nodeInfo = hierarchyMap[nodeId];
      
      // Use stored position if available, otherwise calculate new position
      let nodePosition;
      if (storedPositions[nodeId]) {
        // Keep existing position from storage (works for both visible and previously hidden nodes)
        nodePosition = snapPositionToGrid(storedPositions[nodeId]);
      } else {
        // Calculate position for new node - position it relative to parent if possible
        if (nodeInfo.parentId && storedPositions[nodeInfo.parentId]) {
          // Position new node below and slightly offset from parent
          const parentPos = snapPositionToGrid(storedPositions[nodeInfo.parentId]);
          const siblings = hierarchyMap[nodeInfo.parentId].childrenIds.filter(id => visibleNodeIds.has(id));
          const siblingIndex = siblings.indexOf(nodeId);
          
          nodePosition = snapPositionToGrid({
            x: parentPos.x + (siblingIndex - (siblings.length - 1) / 2) * horizontalSpacing,
            y: parentPos.y + verticalSpacing
          });
        } else {
          // Fallback: use level-based positioning
          const levelNum = nodeInfo.level;
          const nodesAtSameLevel = Array.from(visibleNodeIds).filter(id => hierarchyMap[id].level === levelNum);
          const indexAtLevel = nodesAtSameLevel.indexOf(nodeId);
          
          nodePosition = snapPositionToGrid({
            x: indexAtLevel * horizontalSpacing,
            y: levelNum * verticalSpacing
          });
        }
      }

      nodePosition = claimFreePosition(nodePosition);
      
      positionedNodes[nodeInfo.id] = {
        id: nodeInfo.id,
        type: 'custom',
        position: nodePosition,
        data: {
          id: nodeInfo.id,
          nom: nodeInfo.nom,
          prenom: nodeInfo.prenom,
          poste: nodeInfo.poste,
          service: nodeInfo.service,
          societe: nodeInfo.societe,
          photo_url: nodeInfo.photo_url,
          hasChildren: nodeInfo.hasChildren,
          isExpanded: expandedNodes.has(nodeInfo.id),
          isCurrentUser: currentUserId && nodeInfo.id === currentUserId,
          onToggleExpand: toggleNodeExpansion
        }
      };
    });

    newNodes.push(...Object.values(positionedNodes));

    // Build edges between visible nodes
    visibleNodeIds.forEach(nodeId => {
      const nodeInfo = hierarchyMap[nodeId];
      if (nodeInfo.parentId && visibleNodeIds.has(nodeInfo.parentId)) {
        edges.push({
          id: `e-${nodeInfo.parentId}-${nodeId}`,
          source: nodeInfo.parentId,
          target: nodeId,
          type: 'default',
          animated: true,
          markerEnd: {
            type: MarkerType.ArrowClosed,
          },
          style: { 
            stroke: '#3b82f6',
            strokeWidth: 4
          }
        });
      }
    });

    const resolvedPositions = Object.fromEntries(
      newNodes.map((node) => [node.id, snapPositionToGrid(node.position)])
    );

    return { nodes: newNodes, edges, resolvedPositions };
  }, []);

  const toggleNodeExpansion = useCallback((nodeId) => {
    setExpandedNodes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(nodeId)) {
        newSet.delete(nodeId);
      } else {
        newSet.add(nodeId);
      }
      return newSet;
    });
  }, []);

  // Update nodes and edges when expansion state or company filter changes
  useEffect(() => {
    if (Object.keys(hierarchyMap).length > 0) {
      const { nodes: newNodes, edges: newEdges, resolvedPositions } = getVisibleNodesAndEdges(
        hierarchyMap,
        expandedNodes,
        allNodePositionsRef.current,
        selectedCompanies,
        currentUserId,
      );
      if (resolvedPositions) {
        allNodePositionsRef.current = {
          ...allNodePositionsRef.current,
          ...resolvedPositions,
        };
      }
      setNodes(newNodes);
      setEdges(newEdges);
    }
  }, [expandedNodes, hierarchyMap, selectedCompanies, currentUserId, getVisibleNodesAndEdges, setNodes, setEdges]);

  // Sync allNodePositions with current visible nodes positions (when nodes are moved)
  const onNodesChangeWithSync = useCallback((changes) => {
    const snappedChanges = changes.map((change) => {
      if (change.type === 'position' && change.position) {
        return { ...change, position: snapPositionToGrid(change.position) };
      }
      return change;
    });
    onNodesChange(snappedChanges);
    
    // Update allNodePositions when nodes are moved
    snappedChanges.forEach(change => {
      if (change.type === 'position' && change.dragging === false) {
        // Only update when drag is complete - use ref to avoid re-renders
        setNodes(currentNodes => {
          const node = currentNodes.find(n => n.id === change.id);
          if (node && node.position) {
            const snapped = snapPositionToGrid(node.position);
            allNodePositionsRef.current = {
              ...allNodePositionsRef.current,
              [change.id]: snapped
            };
            if (snapped.x !== node.position.x || snapped.y !== node.position.y) {
              return currentNodes.map((n) =>
                n.id === change.id ? { ...n, position: snapped } : n
              );
            }
          }
          return currentNodes;
        });
      }
    });
  }, [onNodesChange, setNodes]);

  const fetchOrgChart = async () => {
    try {
      // Fetch org chart data and saved layout
      const [orgChartRes, layoutRes] = await Promise.all([
        axios.get(`${API_URL}/api/org-chart`),
        axios.get(`${API_URL}/api/org-chart/layout`)
      ]);
      
      const orgChartData = orgChartRes.data;
      setOrgData(orgChartData);
      
      // Build hierarchy map
      const hMap = buildHierarchyMap(orgChartData);
      setHierarchyMap(hMap);
      
      // Find current user in hierarchy
      let currentUserNodeId = null;
      const userCompanies = new Set();
      
      if (user) {
        // Store current user ID
        setCurrentUserId(user.id);
        
        // Find user in hierarchy and collect their company
        Object.values(hMap).forEach(nodeInfo => {
          if (nodeInfo.id === user.id && nodeInfo.societe) {
            currentUserNodeId = nodeInfo.id;
            userCompanies.add(nodeInfo.societe);
          }
        });
      }
      
      // Initialize company filter
      const companies = new Set();
      if (userCompanies.size > 0) {
        // Filter by user's company/companies only
        userCompanies.forEach(c => companies.add(c));
      } else {
        // Fallback: show all companies if user not found or has no company
        Object.values(hMap).forEach(nodeInfo => {
          if (nodeInfo.societe) {
            companies.add(nodeInfo.societe);
          }
        });
      }
      setSelectedCompanies(companies);
      
      // Determine initial expansion state
      let initialExpandedNodes = new Set();
      
      if (layoutRes.data.expanded_nodes && Array.isArray(layoutRes.data.expanded_nodes)) {
        // Use saved expansion state
        initialExpandedNodes = new Set(layoutRes.data.expanded_nodes);
        setHasCustomLayout(true);
      } else {
        // Expand level 0 nodes
        Object.values(hMap).forEach(nodeInfo => {
          if (nodeInfo.level === 0) {
            initialExpandedNodes.add(nodeInfo.id);
          }
        });
        setHasCustomLayout(false);
      }
      
      // Expand path to current user
      if (currentUserNodeId) {
        let currentId = currentUserNodeId;
        while (currentId) {
          const nodeInfo = hMap[currentId];
          if (!nodeInfo) break;
          
          // Expand parent node
          if (nodeInfo.parentId) {
            initialExpandedNodes.add(nodeInfo.parentId);
          }
          
          currentId = nodeInfo.parentId;
        }
      }
      
      setExpandedNodes(initialExpandedNodes);
      
      // Initialize allNodePositions with saved positions (alignées sur la grille)
      const savedPositions = layoutRes.data.positions || {};
      const snappedPositions = Object.fromEntries(
        Object.entries(savedPositions).map(([id, pos]) => [id, snapPositionToGrid(pos)])
      );
      allNodePositionsRef.current = snappedPositions;
      
      // Build visible nodes and edges using stored positions and company filter
      const { nodes: newNodes, edges: newEdges, resolvedPositions } = getVisibleNodesAndEdges(
        hMap,
        initialExpandedNodes,
        snappedPositions,
        companies,
        user?.id,
      );
      if (resolvedPositions) {
        allNodePositionsRef.current = {
          ...snappedPositions,
          ...resolvedPositions,
        };
      }
      
      setNodes(newNodes);
      setEdges(newEdges);
      setLoading(false);
    } catch (error) {
      toast.error('Erreur lors du chargement de l\'organigramme');
      setLoading(false);
    }
  };

  const handleSaveLayout = async () => {
    try {
      setSaving(true);
      
      // Save ALL positions (from allNodePositionsRef), not just visible nodes
      const positions = Object.entries(allNodePositionsRef.current).map(([id, pos]) => {
        const snapped = snapPositionToGrid(pos);
        return {
          id,
          x: snapped.x,
          y: snapped.y
        };
      });
      allNodePositionsRef.current = Object.fromEntries(
        positions.map(({ id, x, y }) => [id, { x, y }])
      );
      
      const expanded_nodes = Array.from(expandedNodes);
      
      await axios.post(`${API_URL}/api/org-chart/save-layout`, { 
        positions,
        expanded_nodes
      });
      toast.success('Disposition sauvegardée avec succès !');
      setHasCustomLayout(true);
      setSaving(false);
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
      setSaving(false);
    }
  };

  const handleResetLayout = async () => {
    if (!window.confirm('Voulez-vous vraiment réinitialiser la disposition ?')) {
      return;
    }
    
    try {
      await axios.delete(`${API_URL}/api/org-chart/layout`);
      toast.success('Disposition réinitialisée !');
      // Reload the org chart with automatic layout
      fetchOrgChart();
    } catch (error) {
      toast.error('Erreur lors de la réinitialisation');
    }
  };
  
  const toggleCompany = (company) => {
    setSelectedCompanies(prev => {
      const newSet = new Set(prev);
      if (newSet.has(company)) {
        newSet.delete(company);
      } else {
        newSet.add(company);
      }
      return newSet;
    });
  };
  
  const selectAllCompanies = () => {
    setSelectedCompanies(new Set(availableCompanies));
  };
  
  const deselectAllCompanies = () => {
    setSelectedCompanies(new Set());
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Chargement de l'organigramme...</p>
        </div>
      </div>
    );
  }

  return (
    <AppLayout>
    <div className="h-full flex flex-col">
      {/* Page Header */}
      <div className="border-b border-border bg-card/50 backdrop-blur-sm">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Organigramme Hiérarchique
              </h1>
              {hasCustomLayout && (
                <p className="text-xs text-muted-foreground">Disposition personnalisée</p>
              )}
            </div>
            <div className="flex items-center gap-2">
              {canModifyOrgchartLayout && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleSaveLayout}
                  disabled={saving}
                  data-testid="save-layout-button"
                >
                  {saving ? 'Sauvegarde...' : '💾 Sauvegarder'}
                </Button>
              )}
              {canModifyOrgchartLayout && hasCustomLayout && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleResetLayout}
                  data-testid="reset-layout-button"
                >
                  🔄 Réinitialiser
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Company Filter */}
      {availableCompanies.length > 0 && (
        <div className="border-b border-border company-filter-transparent">
          <div className="container mx-auto px-6 py-3">
            <div className="flex items-center gap-4 flex-wrap">
              <Building2 className="h-4 w-4 text-muted-foreground" />
              
              <div className="flex items-center gap-2 flex-wrap">
                {availableCompanies.map(company => (
                  <button
                    key={company}
                    onClick={() => toggleCompany(company)}
                    className={`
                      inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium
                      transition-all duration-200 border
                      ${selectedCompanies.has(company)
                        ? 'bg-primary text-primary-foreground border-primary hover:bg-primary/90'
                        : 'bg-muted text-muted-foreground border-border hover:bg-muted/80'
                      }
                    `}
                    data-testid={`company-filter-${company.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    <div className={`
                      w-4 h-4 rounded border-2 flex items-center justify-center
                      ${selectedCompanies.has(company)
                        ? 'border-primary-foreground bg-primary-foreground/20'
                        : 'border-muted-foreground/40'
                      }
                    `}>
                      {selectedCompanies.has(company) && (
                        <svg className="w-3 h-3" viewBox="0 0 12 12" fill="none">
                          <path
                            d="M10 3L4.5 8.5L2 6"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      )}
                    </div>
                    {company}
                  </button>
                ))}
              </div>
              
              <div className="flex items-center gap-2 ml-auto">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={selectAllCompanies}
                  className="text-xs"
                  data-testid="select-all-companies"
                >
                  Tout sélectionner
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={deselectAllCompanies}
                  className="text-xs"
                  data-testid="deselect-all-companies"
                >
                  Tout désélectionner
                </Button>
              </div>
            </div>
            
            {selectedCompanies.size === 0 && (
              <div className="mt-2 text-xs text-amber-600 dark:text-amber-500">
                ⚠️ Aucune entreprise sélectionnée - l'organigramme est vide
              </div>
            )}
          </div>
        </div>
      )}

      {/* ReactFlow Chart */}
      <div className="flex-1">
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChangeWithSync}
          onEdgesChange={onEdgesChange}
          nodeTypes={nodeTypes}
          nodesDraggable={canModifyOrgchartLayout}
          snapToGrid
          snapGrid={ORG_CHART_SNAP_GRID}
          fitView
          attributionPosition="bottom-left"
        >
          <Background gap={ORG_CHART_GRID_SIZE} size={1} />
          <Controls />
          <MiniMap nodeStrokeWidth={3} zoomable pannable />
        </ReactFlow>
      </div>
    </div>
    </AppLayout>
  );
};

export default OrgChartPage;